/**
 * The contrast rules as lint rather than documentation — requirement 6.10.
 *
 * The design's rule is one sentence: **on dark surfaces, red is a fill, not an ink.** Red on
 * `navyDeep` measures 2.94:1, which fails even the 3:1 non-text threshold, so red text, red icons,
 * and red borders on navy do not meet contrast and must not be used. They look plausible in a design
 * tool and are unreadable in production, which is exactly the class of decision a lint rule is for.
 *
 * Nothing here is a transcribed number. Every pairing is computed from `themes.ts`, so a future
 * lightness nudge that drops a foreground below AA fails the build instead of shipping.
 *
 * ## How a token earns the right to be an ink
 *
 * A primitive in this repo is theme-agnostic by construction — one shared component, both themes, no
 * per-app override (requirement 7.6). So a colour is only usable as an ink if it is usable in *both*
 * themes. That is what disqualifies `primary`: red clears 5.14:1 on the light theme's white page and
 * 2.94:1 on the dark theme's navy one, and a component cannot know which one it is on.
 *
 * Checked against the **neutral** surfaces — the ones a component can land on without asking:
 * `background`, `card`, `popover`, `muted`, `secondary`. The three **stated** surfaces (`primary`,
 * `accent`, `destructive`) are excluded from that sweep on purpose: writing `bg-primary` is an
 * explicit choice that comes with `text-primary-foreground`, and holding a general-purpose ink to
 * "also legible on gold" would reject `text-foreground` — the single most-used utility in the system.
 * Those three are covered instead by the surface/ink matrix below, where each is paired with the
 * foreground token that exists for it.
 *
 * ## Two classes of use, because WCAG has two thresholds
 *
 * - **ink** — text and meaningful icons: `text-*`, `fill-*`, `stroke-*`, `decoration-*`,
 *   `placeholder-*`, `caret-*`. 4.5:1.
 * - **boundary** — focus rings, control edges, anything carrying meaning through a line:
 *   `border-*`, `divide-*`, `outline-*`, `ring-*`. 3:1.
 *
 * Everything else — `bg-*`, gradient stops — is a fill with no threshold of its own, because what
 * sits on top of it is what gets checked.
 *
 * ## The one exemption, and one honest limit
 *
 * `border` is exempt from the 3:1 boundary threshold. A card edge drawn at 3:1 against its own page
 * reads as a box rather than an edge, and a separator between two adjacent surfaces is not "visual
 * information required to identify a component" under 1.4.11. It is one named entry so the exemption
 * stays one line in one place rather than becoming a habit. `input` and `ring` — the two boundaries
 * that *are* meaningful — carry the full 3:1 in both themes and are asserted to.
 *
 * The limit: an opacity modifier (`border-foreground/10`) lowers the real ratio below anything
 * computable from the token alone. Static analysis settles the token; the rendered result needs a
 * real browser, which is where the design puts it.
 */
import { semanticTokenNames } from '@apuestapr/config/tailwind-tokens';
import { brandPalette, resolveDerivedColor } from './brand.js';
import { contrastRatio, thresholds } from './contrast.js';
import { themes, themeNames } from './themes.js';
/** Every colour token's resolved triplet in one theme. Brand tokens are theme-invariant. */
export const resolvedColors = (theme) => {
    const brand = Object.entries(brandPalette).map(([name, color]) => [name, color.triplet]);
    const semantic = Object.entries(themes[theme].colors).map(([name, value]) => [
        name,
        resolveDerivedColor(value),
    ]);
    return Object.fromEntries([...brand, ...semantic]);
};
/** Surfaces a component can land on without opting in. */
export const neutralSurfaceTokens = [
    'background',
    'card',
    'popover',
    'muted',
    'secondary',
];
/** Surfaces you choose deliberately, and whose ink you choose in the same breath. */
export const statedSurfaceTokens = [
    'primary',
    'accent',
    'destructive',
];
export const surfaceTokens = [
    ...neutralSurfaceTokens,
    ...statedSurfaceTokens,
];
/** The declared ink for each surface — the pairing the design is actually promising. */
export const surfaceInk = {
    background: 'foreground',
    card: 'card-foreground',
    popover: 'popover-foreground',
    muted: 'muted-foreground',
    secondary: 'secondary-foreground',
    primary: 'primary-foreground',
    accent: 'accent-foreground',
    destructive: 'destructive-foreground',
};
/** Boundary tokens held to the full 3:1 against every neutral surface, in both themes. */
export const meaningfulBoundaryTokens = ['input', 'ring'];
/** See the header. One entry, and it is the whole exemption list. */
export const decorativeBoundaryTokens = ['border'];
/**
 * Tokens that are ink by definition: `foreground` and every `*-foreground`. Their correctness is
 * established by the surface/ink matrix rather than by the general sweep, because each one is
 * defined against exactly one surface.
 */
export const declaredInkTokens = semanticTokenNames.filter((name) => name === 'foreground' || name.endsWith('-foreground'));
/** One token against every neutral surface in one theme. */
export const neutralPairings = (theme, ink) => {
    const colors = resolvedColors(theme);
    return neutralSurfaceTokens.map((surface) => ({
        theme,
        ink,
        surface,
        ratio: contrastRatio(colors[ink], colors[surface]),
    }));
};
/** The worst neutral-surface pairing across both themes — what decides whether a token may be ink. */
export const worstNeutralPairing = (ink) => themeNames
    .flatMap((theme) => neutralPairings(theme, ink))
    .reduce((worst, candidate) => (candidate.ratio < worst.ratio ? candidate : worst));
/** Every declared surface/ink pairing, both themes. The matrix requirement 6.10 is really about. */
export const declaredPairings = () => themeNames.flatMap((theme) => {
    const colors = resolvedColors(theme);
    return surfaceTokens.map((surface) => ({
        theme,
        ink: surfaceInk[surface],
        surface,
        ratio: contrastRatio(colors[surfaceInk[surface]], colors[surface]),
    }));
});
const candidateTokens = () => [
    ...Object.keys(brandPalette),
    ...semanticTokenNames.filter((name) => !declaredInkTokens.includes(name)),
];
/** Computed, not listed: tokens clearing `threshold` on every neutral surface in every theme. */
export const tokensClearing = (threshold) => candidateTokens().filter((name) => worstNeutralPairing(name).ratio >= threshold);
/** Tokens allowed in an ink position — text, meaningful icons. */
export const inkAllowlist = () => [
    ...declaredInkTokens,
    ...tokensClearing(thresholds.text),
];
/** Tokens allowed in a boundary position — borders, dividers, outlines, focus rings. */
export const boundaryAllowlist = () => [
    ...declaredInkTokens,
    ...decorativeBoundaryTokens,
    ...tokensClearing(thresholds.graphic),
];
//# sourceMappingURL=ink-policy.js.map